import { Chip, Stack, TableCell, Typography, IconButton } from '@mui/material';

import { getCsatKeywordHits } from 'src/pages/csat/utils/csat-analysis-utils';
import { CsatSourceBadge } from 'src/pages/csat/components/csat-source-badge';
import { AgentAvatar } from 'src/pages/csat/components/csat-survey-details-dialog';

import { Iconify } from 'src/components/iconify';

import { RowCellDate } from './row-cell-date';

// ----------------------------------------------------------------------

type Props = {
  columnId: string;
  row: any;
  headerIndex: number;
  t?: any;
  handlePreview?: (row: any) => void;
};

export function RowCellCsat({ columnId, row, headerIndex, t, handlePreview }: Props) {
  const value = row[columnId];

  if (columnId === 'createdAt') {
    return (
      <TableCell key={columnId || headerIndex} sx={{ whiteSpace: 'nowrap' }}>
        <RowCellDate value={value} fieldKey={columnId} />
      </TableCell>
    );
  }

  if (columnId === 'rating') {
    return (
      <TableCell key={columnId || headerIndex} sx={{ fontSize: 12 }}>
        {value}
      </TableCell>
    );
  }

  if (columnId === 'agent') {
    const agent = row.agent;
    return (
      <TableCell key={columnId || headerIndex} sx={{ whiteSpace: 'nowrap' }}>
        {agent ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <AgentAvatar
              profileUrl={agent.profileUrl}
              name={agent.name || agent.firstName}
              sx={{ width: 24, height: 24, fontSize: '0.75rem', fontWeight: 600 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>
              {agent.name || `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim() || 'Agent'}
            </Typography>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 12 }}
          >
            {t ? t('UNASSIGNED') : 'Unassigned'}
          </Typography>
        )}
      </TableCell>
    );
  }

  if (columnId === 'feedback') {
    return (
      <TableCell key={columnId || headerIndex} sx={{ maxWidth: 280, fontSize: 12 }}>
        {value ? `${value.slice(0, 120)}${value.length > 120 ? '…' : ''}` : '—'}
      </TableCell>
    );
  }

  if (columnId === 'keywordHits') {
    const hits = getCsatKeywordHits(row.metadata);
    return (
      <TableCell key={columnId || headerIndex} sx={{ maxWidth: 240, fontSize: 12 }}>
        {hits.length ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {hits.map((hit) => (
              <Chip
                key={hit.keyword}
                size="small"
                color="warning"
                variant="soft"
                label={hit.count > 1 ? `${hit.keyword} ×${hit.count}` : hit.keyword}
                sx={{ fontSize: 11, fontWeight: 600 }}
              />
            ))}
          </Stack>
        ) : (
          '—'
        )}
      </TableCell>
    );
  }

  if (columnId === 'source') {
    return (
      <TableCell key={columnId || headerIndex}>
        <CsatSourceBadge source={value} />
      </TableCell>
    );
  }

  if (columnId === 'sentimentAtClose') {
    return (
      <TableCell key={columnId || headerIndex} sx={{ textTransform: 'capitalize', fontSize: 12 }}>
        {value ?? '—'}
      </TableCell>
    );
  }

  if (columnId === 'sentimentScore') {
    return (
      <TableCell key={columnId || headerIndex} sx={{ fontSize: 12 }}>
        {value != null ? value.toFixed(2) : '—'}
      </TableCell>
    );
  }

  if (columnId === 'conversationId') {
    return (
      <TableCell key={columnId || headerIndex} sx={{ fontFamily: 'monospace', fontSize: 11 }}>
        {value ? `${value.slice(0, 8)}…` : '—'}
      </TableCell>
    );
  }

  if (columnId === 'action') {
    return (
      <TableCell key={columnId || headerIndex} align="right">
        <IconButton size="small" onClick={() => handlePreview?.(row)}>
          <Iconify icon="mdi:eye" width={20} />
        </IconButton>
      </TableCell>
    );
  }

  return null;
}
